import React from 'react';
import { Camera } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div style={{
      background: 'linear-gradient(to bottom, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
      minHeight: '100vh',
      color: '#fff',
      fontFamily: "'Work Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Work+Sans:wght@300;400;500;600&display=swap');
      `}</style>

      {/* Nav */}
      <nav style={{
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(10,10,10,0.9)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera size={18} color="white" />
            </div>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 700, color: '#fff' }}>PhotoVoice</span>
          </a>
          <a href="/" style={{
            fontSize: 14, color: '#9ca3af', textDecoration: 'none',
            padding: '8px 16px', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 50, transition: 'all 0.2s',
          }}
            onMouseOver={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
            onMouseOut={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
          >
            ← Back to home
          </a>
        </div>
      </nav>

      {/* Content */}
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px' }}>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(36px, 5vw, 52px)',
          fontWeight: 700,
          marginBottom: 8,
        }}>Privacy Policy</h1>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 48 }}>Effective Date: 19 Mar 2026</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>

          <Section title="1. Introduction">
            PhotoVoice.app ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website{' '}
            <a href="https://www.photovoice.app" style={{ color: '#a78bfa' }}>https://www.photovoice.app</a>{' '}
            to register your interest in the beta test of our mobile application (the "App"). Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the site or submit your information.
          </Section>

          <Section title="2. Information We Collect">
            We collect personal information that you voluntarily provide to us when you register interest in our beta program. The information we collect is limited to your email address. We do not currently collect any other personal information through this landing page.
          </Section>

          <Section title="3. How We Use Your Information">
            <p style={{ marginBottom: 16 }}>We use the information we collect for the following purposes, which are necessary for our legitimate business interests (under GDPR) and/or to fulfill our contract with you:</p>
            <ul style={{ paddingLeft: 20, lineHeight: 2, color: '#9ca3af' }}>
              <li><strong style={{ color: '#fff' }}>To Administer the Beta Program:</strong> To send you an invitation link to join the beta test, to communicate with you about the beta program, and to provide you with information about the App.</li>
              <li><strong style={{ color: '#fff' }}>For Improvement and Development:</strong> To analyze interest in the App, to gather feedback for improving the App and related products, and to conduct research and development.</li>
              <li><strong style={{ color: '#fff' }}>To Communicate With You:</strong> To contact you with surveys, inquiries, or important information related to your beta testing experience or changes to our policies.</li>
              <li><strong style={{ color: '#fff' }}>For Security and Legal Compliance:</strong> To protect against security threats, fraud, and to comply with applicable legal obligations.</li>
            </ul>
          </Section>

          <Section title="4. Legal Basis for Processing (For European Users)">
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, our legal basis for collecting and using your personal information is your consent. By submitting your email address through the sign-up form, you explicitly consent to our collection and use of your information for the purposes described in this policy. You have the right to withdraw your consent at any time.
          </Section>

          <Section title="5. Sharing Your Information">
            <p style={{ marginBottom: 16 }}>We respect your privacy. We do not sell, rent, or trade your personal information to third parties for their own marketing purposes. We may share your information in the following limited circumstances:</p>
            <ul style={{ paddingLeft: 20, lineHeight: 2, color: '#9ca3af' }}>
              <li><strong style={{ color: '#fff' }}>Service Providers:</strong> We may share your information with third-party vendors who perform services on our behalf, such as email communication platforms or data storage services. These service providers are contractually obligated to protect your information and use it only for the specified purpose.</li>
              <li><strong style={{ color: '#fff' }}>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
              <li><strong style={{ color: '#fff' }}>Business Transfers:</strong> In the event of a merger, acquisition, or asset sale, your information may be transferred. We will provide notice before your information is transferred and becomes subject to a different privacy policy.</li>
            </ul>
          </Section>

          <Section title="6. Data Security and Retention">
            We implement reasonable and appropriate security measures to protect your personal information from unauthorized access, use, or disclosure. We will retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, such as for the duration of the beta testing period and a reasonable time thereafter for analysis and follow-up, unless a longer retention period is required or permitted by law.
          </Section>

          <Section title="7. Your Privacy Rights">
            <p style={{ marginBottom: 16 }}>Depending on your location, you may have specific rights regarding your personal information.</p>
            <ul style={{ paddingLeft: 20, lineHeight: 2, color: '#9ca3af' }}>
              <li><strong style={{ color: '#fff' }}>For All Users:</strong> You may contact us to access, correct, or delete your personal information.</li>
              <li><strong style={{ color: '#fff' }}>For European Users (GDPR):</strong> You have the right to request access to, rectification, or erasure of your personal data; data portability; and the right to object to or restrict processing.</li>
              <li><strong style={{ color: '#fff' }}>For California Users (CCPA):</strong> If you are a California resident, you have the right to request that we disclose what personal information we collect, use, and share; the right to request deletion of your personal information; and the right to opt-out of the "sale" of your personal information. We do not sell personal information.</li>
            </ul>
            <p style={{ marginTop: 16 }}>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@photovoice.app" style={{ color: '#a78bfa' }}>support@photovoice.app</a>.
              We will respond to your request in accordance with applicable law.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            Our website and beta program are not intended for individuals under the age of 16. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe we have collected information from a child, please contact us so we can take appropriate action.
          </Section>

          <Section title="9. Changes to This Privacy Policy">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page with a new effective date. We encourage you to review this Privacy Policy periodically for any changes.
          </Section>

          <Section title="10. Contact Us">
            If you have questions or comments about this Privacy Policy or our privacy practices, please contact us at:{' '}
            <a href="mailto:support@photovoice.app" style={{ color: '#a78bfa' }}>support@photovoice.app</a>.
          </Section>

        </div>
      </main>

      <footer style={{ padding: '32px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
        <p style={{ color: '#374151', fontSize: 13 }}>© 2026 PhotoVoice. All rights reserved.</p>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: 22,
        fontWeight: 700,
        marginBottom: 16,
        color: '#fff',
      }}>{title}</h2>
      <div style={{ color: '#9ca3af', lineHeight: 1.8, fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}
